// TAC memberships must retain their own identity, account and property throughout
// selection. Dates are calendar dates; UTC is used only for day arithmetic.
const list = (value) => (Array.isArray(value) ? value : value ? [value] : [])
const clone = (value) => JSON.parse(JSON.stringify(value))
export const tacId = (value) => {
  const id = list(value && value.UniqueID)[0]
  return id && typeof id === "object" ? id.value : id
}
const iso = (value) => String(value || "").slice(0, 10)
const display = (value) => iso(value).split("-").reverse().join(".")
const fromDisplay = (value) => value.split(".").reverse().join("-")
const days = (start, end) => (Date.parse(iso(end)) - Date.parse(iso(start))) / 86400000
const termId = (term, member) => tacId(term.ContractID) || tacId(member.ContractID)
const accountId = (member) => tacId(list(member.Accounts && member.Accounts.Account).find((account) => account.type === "MEMBERSHIP")?.AccountID)
const terms = (member) => list(member.ContractTermTimes && member.ContractTermTimes.ContractTermTime)
const identity = (member) => JSON.stringify([tacId(member.ContractID), accountId(member), member.MemberNumber, tacId(member.PropertyID)])
const termKey = (term, member) => JSON.stringify([termId(term, member), term.Type, iso(term.TimeSpan.Start), iso(term.TimeSpan.End)])

export function sameMembershipCategory(first, second, groups = []) {
  return first === second || groups.some((group) => group.includes(first) && group.includes(second))
}

export function selectMemberships(current, response, groups, today) {
  if (!current || !terms(current).some((term) => term.Type === "DEFAULT")) throw new Error("Missing membership")
  if (!response || !response.MemberDetails || !response.MemberDetails.MemberDetail) throw new Error("Missing membership list")
  const selected = new Map()
  for (const member of [current, ...list(response && response.MemberDetails && response.MemberDetails.MemberDetail)]) {
    const memberTerms = terms(member).filter((term) => term.TimeSpan && term.TimeSpan.Start && term.TimeSpan.End)
    const future = memberTerms.some((term) => term.Type === "DEFAULT" && iso(term.TimeSpan.Start) > today)
    if (identity(member) !== identity(current) && member.MemberCategoryName !== "VIP Import" &&
        !sameMembershipCategory(member.MemberCategoryName, current.MemberCategoryName, groups) && !future) continue
    if (!memberTerms.some((term) => term.Type === "DEFAULT")) continue
    const key = identity(member)
    const existing = selected.get(key)
    const merged = existing ? terms(existing).concat(memberTerms) : memberTerms
    const unique = new Map(merged.map((term) => [termKey(term, member), clone(term)]))
    const copy = existing || clone(member)
    // The all-memberships endpoint uses withProfile=false. The current response
    // supplies the same customer's profile; never substitute account/studio data.
    if (!copy.Profile && current.Profile) copy.Profile = clone(current.Profile)
    copy.ContractTermTimes = { ContractTermTime: [...unique.values()].sort((a, b) =>
      iso(a.TimeSpan.Start).localeCompare(iso(b.TimeSpan.Start)) || (a.Type === "DEFAULT" ? -1 : b.Type === "DEFAULT" ? 1 : 0)) }
    selected.set(key, copy)
  }
  return [...selected.values()]
}

export async function loadMembershipTransactions(memberships, fetchTransactions, start, end) {
  const accounts = new Map()
  for (const member of memberships) {
    if (member.MemberCategoryName === "VIP Import") continue
    const id = accountId(member)
    if (!id) continue // This membership will be marked unavailable below.
    if (!accounts.has(String(id))) {
      try {
        const result = await fetchTransactions(id, start, end)
        const transactions = result && result.AccountTransactions && result.AccountTransactions.AccountTransactions
        if (!Array.isArray(transactions)) throw new Error("Invalid account transactions")
        accounts.set(String(id), clone(transactions))
      } catch (error) {
        // Cache failures too: another membership on this account must not retry
        // it or prevent the remaining accounts from being loaded.
        accounts.set(String(id), null)
      }
    }
  }
  return accounts
}

export function membershipEnd(contract) {
  return [contract.end, ...contract.bonus.map((period) => period.end), ...contract.timeStopBonus.map((period) => period.end)]
    .map(fromDisplay).sort().pop()
}

export function visibleMembershipPeriods(contracts) {
  const ordered = contracts.slice().sort((a, b) => fromDisplay(a.start).localeCompare(fromDisplay(b.start)))
  return [...ordered.filter((contract) => !contract.future).slice(-3), ...ordered.filter((contract) => contract.future)].reverse()
}

export function latestMembershipDate(selected, contracts, groups) {
  if (!selected) return null
  return contracts.filter((contract) =>
    sameMembershipCategory(contract.title, selected.title, groups) &&
    contract.propertyId === selected.propertyId &&
    fromDisplay(contract.start) >= fromDisplay(selected.start))
    .reduce((latest, contract) => membershipEnd(contract) > latest ? membershipEnd(contract) : latest, membershipEnd(selected))
}

export function buildMembershipPeriods(memberships, accountTransactions, normalizeTransactions, today) {
  const result = []
  for (const member of memberships) {
    // Split even a multi-contract TAC response by ContractID before assigning
    // bonus periods, idle periods or payments.
    const byContract = new Map()
    for (const term of terms(member)) {
      const id = termId(term, member)
      if (!byContract.has(id)) byContract.set(id, [])
      byContract.get(id).push(term)
    }
    for (const [contractId, contractTerms] of byContract) {
      const account = accountId(member)
      const raw = member.MemberCategoryName === "VIP Import" ? [] : accountTransactions.get(String(account))
      let paymentLoadError = !Array.isArray(raw)
      let transactions = []
      if (!paymentLoadError) {
        try {
          transactions = normalizeTransactions(clone(raw).filter((transaction) => !tacId(transaction.ContractID) || tacId(transaction.ContractID) === contractId))
        } catch (error) {
          paymentLoadError = true
        }
      }
      const idle = contractTerms.filter((term) => term.Type === "IDLEPERIOD")
      const periods = []
      let period = null
      const within = (transaction, span) => iso(transaction.start) >= iso(span.Start) && iso(transaction.end) <= iso(span.End)
      for (const term of contractTerms) {
        const span = term.TimeSpan
        if (term.Type === "DEFAULT") {
          const eligibleIdle = period ? idle.filter((item) =>
            iso(item.TimeSpan.Start) >= fromDisplay(period.start) && iso(item.TimeSpan.Start) < iso(span.Start)) : []
          const duration = days(span.Start, span.End)
          const total = eligibleIdle.reduce((sum, item) => sum + days(item.TimeSpan.Start, item.TimeSpan.End), 0)
          // Retain TAC's existing duration-based extension recognition, scoped
          // to this contract and its preceding term, never a different product.
          const extension = period && eligibleIdle.length &&
            (eligibleIdle.some((item) => days(item.TimeSpan.Start, item.TimeSpan.End) === duration) || Math.abs(total - duration) <= 1)
          if (extension) {
            period.timeStopBonus.push({ start: display(span.Start), end: display(span.End) })
          } else {
            period = {
              ContractID: contractId,
              membership: member,
              membershipKey: identity(member),
              accountId: account,
              memberNumber: member.MemberNumber,
              propertyId: tacId(member.PropertyID),
              title: member.MemberCategoryName,
              start: display(span.Start), end: display(span.End),
              bonus: [], timeStopBonus: [], idlePeriods: [],
              transactions: transactions.filter((transaction) => transaction.type !== "IDLE_PERIOD" && within(transaction, span)),
              future: iso(span.Start) > today,
              active: false,
              paymentLoadError,
            }
            period.isPaid = !paymentLoadError && !period.transactions.some((transaction) => transaction.type === "OPEN_PAYMENT" && transaction.state !== "PAID")
            periods.push(period)
          }
        } else if (term.Type === "BONUSPERIOD" && period) {
          period.bonus.push({ start: display(span.Start), end: display(span.End) })
        }
      }
      for (const term of idle) {
        const period = periods.find((item) => iso(term.TimeSpan.Start) >= fromDisplay(item.start) && iso(term.TimeSpan.Start) <= membershipEnd(item))
        if (period) period.idlePeriods.push({
          start: display(term.TimeSpan.Start), end: display(term.TimeSpan.End),
          transactions: transactions.filter((transaction) => transaction.type === "IDLE_PERIOD" && within(transaction, term.TimeSpan)),
        })
      }
      result.push(...periods)
    }
  }
  result.sort((a, b) => fromDisplay(a.start).localeCompare(fromDisplay(b.start)))
  const active = result.filter((period) => !period.future).pop()
  if (active) active.active = true
  return result
}
